import type { Prisma, PrismaClient } from "@prisma/client";
import dayjs from "dayjs";
import { z } from "zod";
import type { CareFormCategory } from "./models/CareFormCategory";

export enum OccurrenceType {
	initial = "initial",
	annual = "annual",
	thirtyDay = "30-day",
	sixMonth = "6-month",
}

export const CareFormTypeCreateSchema = z.object({
	name: z
		.string()
		.min(1)
		.regex(/^[a-zA-Z0-9_-]+$/, "Must be alphanumeric and can include - and _"),
	display_name: z.string().min(1),
	completed_once_per_client: z.boolean().optional(),
	days_before_renewal: z.number().int().positive().min(7).nullable().optional(),
	payer_source_ids: z.array(z.coerce.number()).optional(),
});
export type CareFormTypeCreateType = z.infer<typeof CareFormTypeCreateSchema>;

export const CareFormTypeUpdateSchema = CareFormTypeCreateSchema.partial()
	.omit({ name: true })
	.extend({
		id: z.coerce.number(),
		enabled: z.boolean().optional(),
		enabled_date: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}$/)
			.nullable()
			.optional(),
		under_development: z.boolean().optional(),
		original_file_upload_error: z.string().nullable().optional(),
		original_file_uploaded_at: z.string().nullable().optional(),
		pdf_upload_error: z.string().nullable().optional(),
		pdf_uploaded_at: z.string().nullable().optional(),
	});

export type CareFormTypeUpdateType = z.infer<typeof CareFormTypeUpdateSchema>;

export const CareFormTypeBranchSettingsUpsertSchema = z.object({
	care_form_type_id: z.coerce.number(),
	branch_id: z.coerce.number(),
	days_before_renewal: z.number().int().positive().min(7).nullable().optional(),
});

export type CareFormTypeBranchSettingsUpsertType = z.infer<
	typeof CareFormTypeBranchSettingsUpsertSchema
>;

export const CareFormTypeVaMedicalCenterSettingsUpsertSchema = z.object({
	care_form_type_id: z.coerce.number(),
	va_medical_center_id: z.coerce.number(),
	days_before_renewal: z.number().int().positive().min(7).nullable().optional(),
});

export type CareFormTypeVaMedicalCenterSettingsUpsertType = z.infer<
	typeof CareFormTypeVaMedicalCenterSettingsUpsertSchema
>;
export const CareFormCreateSchema = z.object({
	care_form_type_id: z.coerce.number(),
	client_id: z.coerce.number().positive(),
	payer_source_id: z.coerce.number().positive(),
	nurse_payroll_units: z
		.number()
		.int()
		.max(8)
		.nonnegative()
		.nullable()
		.optional(),
	nurse_id: z.coerce.number().positive().nullable().optional(),
	occurrence_type: z.nativeEnum(OccurrenceType).nullable().optional(),
	due_at: z.coerce.date(),
	days_before_renewal: z.coerce
		.number()
		.int()
		.nonnegative()
		.nullable()
		.optional(),
	offline_id: z.string().uuid().optional(),
	care_plan_schedule_config: z
		.object({
			days: z
				.array(
					z.object({
						dayOfWeek: z.number().min(0).max(6),
						startTime: z.string().optional(),
						endTime: z.string().optional(),
					}),
				)
				.default([]),
			tasks: z
				.array(
					z.object({
						category: z.string(),
						name: z.string(),
						minutes: z
							.number()
							.int()
							.nonnegative()
							.max(600)
							.nullable()
							.optional(),
						minutesFrequency: z
							.enum(["perDay", "perWeek"])
							.nullable()
							.optional(),
						times: z.number().int().nonnegative().max(28).nullable().optional(),
						timesFrequency: z.enum(["perDay", "perWeek"]).nullable().optional(),
						days: z.array(z.number().min(0).max(6)).nullable().optional(),
						notes: z.string().max(240).nullable().optional(),
					}),
				)
				.default([]),
			transportation: z
				.object({
					allowed: z.boolean().optional(),
				})
				.nullish(),
		})
		.nullish(),
});

export type CareFormCreateType = z.infer<typeof CareFormCreateSchema>;

export const CareFormSaveCompletedSchema = CareFormCreateSchema.partial()
	.omit({
		care_form_type_id: true,
	})
	.extend({
		id: z.coerce.number(),
		field_data: z
			.record(z.string().or(z.number()).nullable().optional())
			.optional(),
		comment: z.string().max(1000).nullable().optional(),
		client_unable_to_sign_reason: z.string().nullable().optional(),
	});

export type CareFormSaveCompletedType = z.infer<
	typeof CareFormSaveCompletedSchema
>;

export const CareFormAutoSaveSchema = CareFormCreateSchema.partial()
	.omit({
		care_form_type_id: true,
	})
	.extend({
		id: z.coerce.number(),
		field_data: z
			.record(z.string().or(z.number()).nullable().optional())
			.optional(),
		comment: z.string().max(1000).nullable().optional(),
		offline_id: z.string().uuid().optional(),
		client_unable_to_sign_reason: z.string().nullable().optional(),
	})
	.transform((data) => {
		const { field_data, ...rest } = data;
		return {
			...rest,
			...(field_data && Object.keys(field_data).length > 0
				? { field_data }
				: {}),
		};
	});

export type CareFormAutoSaveType = z.infer<typeof CareFormAutoSaveSchema>;
export const CareFormPermissionCreateSchema = z.object({
	resource_care_form_type_id: z.coerce.number().int().positive().nullish(),
	grantee_branch_id: z.coerce.number().int().positive().nullish(),
	grantee_role_id: z.coerce.number().int().positive().nullish(),
	grantee_user_id: z.coerce.number().int().positive().nullish(),
	grant_care_form_admin: z.boolean().optional(),
	grant_care_form_submit: z.boolean().optional(),
	grant_care_form_branch_wide_read: z.boolean().optional(),
	grant_care_form_branch_wide_limited_edit: z.boolean().optional(),
});

export type CareFormPermissionCreateType = z.input<
	typeof CareFormPermissionCreateSchema
>;

export const CareFormPermissionUpdateSchema =
	CareFormPermissionCreateSchema.partial().extend({
		id: z.coerce.number().int().positive(),
	});
export type CareFormPermissionUpdateType = z.input<
	typeof CareFormPermissionUpdateSchema
>;
export enum CareFormStatus {
	todo = "todo",
	inProgress = "in-progress",
	completed = "completed",
	nurseReview = "nurse-review",
	assigneeReview = "assignee-review",
	clientReview = "client-review",
	caregiverReview = "caregiver-review",
	pendingPayroll = "pending-payroll",
}
export enum CareSheetPeriod {
	weekly = "weekly",
	biMonthly = "bi-monthly",
}
export enum CareFormSymbol {
	Circle = "::circled::",
}

/**
 * Client unable to sign reasons that can be used across the application.
 * These values are stored in the database and used in various contexts.
 */
export enum ClientUnableToSignReason {
	PhoneVirtualVisit = "Phone/Virtual Visit",
	PhysicalInability = "Physical inability",
	CognitiveInability = "Cognitive inability",
	ClientHospitalized = "Client hospitalized",
	MedicalEmergency = "Medical emergency or distress",
}

/**
 * All client unable to sign reason values as an array for use in Select components.
 */
export const CLIENT_UNABLE_TO_SIGN_REASONS = [
	ClientUnableToSignReason.PhoneVirtualVisit,
	ClientUnableToSignReason.PhysicalInability,
	ClientUnableToSignReason.CognitiveInability,
	ClientUnableToSignReason.ClientHospitalized,
	ClientUnableToSignReason.MedicalEmergency,
] as const;

export type CareFormGetRequiredFieldsMeta = {
	client_unable_to_sign_reason: string | null;
};
export type CareFormGetCompletedMeta = {
	client_unable_to_sign_reason: string | null;
	nurse_payroll_units: number | null;
	occurrence_type: OccurrenceType | null;
};
export type CareFormGetNurseUnitsRequiredMeta = {
	nurse_payroll_units: number | null;
	occurrence_type: OccurrenceType | null;
};

export function getCareSheetDateRange(
	period: CareSheetPeriod,
	shift: { scheduledStart: Date; timezone: string },
) {
	if (period === CareSheetPeriod.weekly) {
		const localStartAt = dayjs.utc(shift.scheduledStart).tz(shift.timezone);
		const startDate = localStartAt.startOf("week");
		return {
			start: startDate.toDate(),
			end: localStartAt.endOf("week").endOf("day").toDate(),
		};
	}
	if (period === CareSheetPeriod.biMonthly) {
		const localStartDate = dayjs.utc(shift.scheduledStart).tz(shift.timezone);
		return localStartDate.date() <= 15
			? {
					start: localStartDate.startOf("month").toDate(),
					end: localStartDate
						.startOf("month")
						.add(14, "day")
						.endOf("day")
						.toDate(),
				}
			: {
					start: localStartDate.startOf("month").add(15, "day").toDate(),
					end: localStartDate.endOf("month").toDate(),
				};
	}
	throw new Error(`Unknown care sheet period ${period}`);
}

export const constructDateFrom24hrTime = (
	timezone: string,
	dateString: string,
	timeString: string,
) => {
	const date = dayjs.tz(dateString, timezone);
	const [hours, minutes] = timeString.split(":");
	return date.set("hour", Number(hours)).set("minute", Number(minutes));
};

export interface CareSheetInterface<T> {
	perShiftClientSignatureRequired: boolean;
	perShiftClientInitialsRequired: boolean;
	perShiftCaregiverSignatureRequired: boolean;
	perShiftCaregiverInitialsRequired: boolean;
	carePlanRequired: boolean;
	transportationDataSupported: boolean;
	isConsolidated?: boolean;

	getPeriod(): CareSheetPeriod;
	getFinalCaregiverRequiredFieldNames(): (keyof T)[];
	getFinalClientRequiredFieldNames(): (keyof T)[];
	getFinalCaregiverSignatureFieldNames(): (keyof T)[];
	getFinalClientSignatureFieldNames(): (keyof T)[];
	getFinalNurseRequiredFieldNames(): (keyof T)[];
	getFinalNurseSignatureFieldNames(): (keyof T)[];
	getFinalClientSignatureDateFieldData(
		date: Date,
	): Partial<Record<keyof T, string>>;
	getFinalCaregiverSignatureDateFieldData(
		date: Date,
	): Partial<Record<keyof T, string>>;
	getFinalNurseSignatureDateFieldData(
		date: Date,
	): Partial<Record<keyof T, string>>;
	getNurseEditableFieldNames(): (keyof T)[];
	fillWithShifts(
		field_data: T,
		shifts: {
			localDate: string;
			caregiverName: string;
			authNumber?: string;
			serviceCode?: string;
			clientICN?: string;
			localTimeIn?: string;
			localTimeOut?: string;
			totalHours?: string;
			clientInitials?: string;
			clientSignature?: string;
			clientSignatureLocalDate?: string;
			clientUnableToSignReason?: string;
			caregiverSignature?: string;
			caregiverInitials?: string;
			caregiverComment?: string;
			caregiverVerbalConfirmation?: string;
			clientVerbalConfirmation?: string;
			completedTasks?: {
				timeMinutes?: number;
				category: string;
				name: string;
				notes?: string;
			}[];
			transportationData?: {
				startTime: string;
				endTime: string;
				totalMiles: number;
				destinationAndPurpose: string;
				errandsTime: number;
				activitiesTime: number;
				wasClientWithYou: boolean;
				totalMinutes: number;
			}[];
		}[],
	): T;
}

export interface CarePlanInterface<T, C> {
	getCareSheetNames(): string[];
	getAssignedTasks(
		field_data: T,
		opts: {
			careSheetName: string;
		},
	): {
		category: string;
		name: string;
		note?: string;
		noteRequired?: boolean;
		timeRequired?: boolean;
		required?: boolean;
	}[];
	// optional async support + ctx for highly specific use cases
	getCareSheetInitialData(
		carePlan: {
			schedule_config: PrismaJson.CarePlanScheduleConfig | null;
			field_data: T;
			client_id: number;
		},
		ctx: { prisma: PrismaClient },
	): Partial<C> | Promise<Partial<C>>;
	pdfPageRange?: { start: number; end: number };
	scheduleConfigRequired?: boolean;
	getScheduleConfigFields(): {
		name: keyof T;
		type:
			| "dayOfWeek"
			| "hoursPerDay"
			| "checkIn"
			| "checkOut"
			| "taskFrequency"
			| "taskNotes"
			| "transportation";
		taskCategory?: string;
		taskName?: string;
		taskMinutesRequired?: boolean;
		taskNotesFieldName?: keyof T;
	}[];
	getPlanPeriodFields?: () => {
		name: keyof T;
		type: "MonthYear";
	}[];
}

/**
 * Utility function to create getRequiredFields implementations with proper client_unable_to_sign filtering.
 * Eliminates code duplication across care form implementations.
 *
 * @param fields - Array of field definitions with name and filledBy properties
 * @returns Function that filters fields based on meta.client_unable_to_sign
 */
export function createGetRequiredFields<T>(
	fields: readonly {
		name: keyof T;
		filledBy: "client" | "nurse" | "caregiver";
	}[],
) {
	return (meta: CareFormGetRequiredFieldsMeta) =>
		fields.filter(
			(f) => !meta.client_unable_to_sign_reason || f.filledBy !== "client",
		);
}

export function hasValidNurseUnits(
	meta: CareFormGetNurseUnitsRequiredMeta,
): boolean {
	return meta?.nurse_payroll_units !== null && meta.nurse_payroll_units > 0;
}

export interface CareFormInterface<T> {
	categories: CareFormCategory[];
	getName(): string;
	getFieldNames(): string[];
	// US state codes this form type is compatible with (lowercase).
	// undefined means compatible with all states.
	states?: string[];
	due_date_label: string;
	autoSetDueAtToCompletedAt?: boolean;
	parseFieldData(
		field_data: Record<string, string | number | null | undefined>,
	): T;
	requireDueDateOnCreate?: boolean;
	getNextDueDate?(data: {
		current_due_at: Date;
		completed_at: Date;
		days_before_renewal: number;
	}): Date | null;
	getRequiredFields(meta: CareFormGetRequiredFieldsMeta): {
		name: keyof T;
		filledBy: "client" | "nurse" | "caregiver";
	}[];
	getSignatureFieldNames(): (keyof T)[];
	getSignatureDisclaimer?(): string;
	getCompleted(field_data: T, meta: CareFormGetCompletedMeta): boolean;
	getAutoFilledFieldData(
		client: Prisma.clientsGetPayload<{
			include: {
				client_addresses: {
					where: {
						is_current: true;
					};
				};
				client_authorizations: {
					select: {
						number: true;
						start_date: true;
					};
				};
			};
		}>,
		previousCompletedCareForm: Prisma.care_formsGetPayload<true> | null,
		caregiver: Prisma.caregiversGetPayload<true> | null,
	): T;
	getClientFileType():
		| "ADR"
		| "Client Rights"
		| "DNR Form"
		| "Expectations"
		| "Initial Evaluation"
		| "PERS Document"
		| "Plan of Care"
		| "POA/ MPOA"
		| "RN Assessment"
		| "Service Agreement"
		| "Authorization"
		| "Referral"
		| "Survey"
		| "Review"
		| "Medication"
		| "RN Contact"
		| "Care Sheet"
		| "CCW PEPST";
	caresheet?: CareSheetInterface<T>;
	careplan?: CarePlanInterface<T, unknown>;
	getNurseUnitsRequired?(meta: CareFormGetNurseUnitsRequiredMeta): boolean;
	occurrenceTypeRequired?: boolean;
	getAllowedOccurrenceTypes?(): OccurrenceType[];
	daysBeforeRenewalRequired?: boolean;
	getMaxDaysBeforeRenewal?: (
		occurrence_type: OccurrenceType | null,
	) => number | undefined;
	getEditableMetadataFields?(): (keyof T)[];
	fillDependencies?: Partial<Record<FillDependency, (keyof T)[]>>;
	redactedFieldsForCaregiver?: (keyof T)[];
	caregiverFormSpec?: {
		fields: CaregiverFormField[];
		requiredPerCaregiverClientCombo?: boolean;
		getCaregiverFormFieldData(input: { signature: string | null }): Partial<T>;
		requiresCarePlan?: boolean;
	};
}

export type CaregiverFormField =
	| { type: "care_plan_ack" }
	| { type: "signature"; label: string };

export type TaskStats = {
	category: string;
	name: string;
	totalMinutes: number;
	totalOccurrences: number;
	isOverMinutes: boolean;
	isOverOccurrences: boolean;
	isWrongDay: boolean;
	minutesLimit?: number | null;
	minutesFrequency?: "perDay" | "perWeek" | null;
	occurrencesLimit?: number | null;
	occurrencesFrequency?: "perDay" | "perWeek" | null;
	allowedDays?: number[] | null;
};
export type FillDependency =
	| "client.icn"
	| "client.name"
	| "client_authorization.number"
	| "client.medicaid_id";
